import { Alert, Box, Button, Dialog, Paper, Typography } from "@mui/material";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import { STATUS_META, isRejectableStatus } from "../constants";

interface RejectLeadsModalProps {
  data: LeadsManagementData;
}

export default function RejectLeadsModal({ data }: RejectLeadsModalProps) {
  const { showRejectConfirm, selectedLeads, cancelBulkReject, confirmBulkReject, isRejecting, canRejectLeads } = data;

  const rejectableSelected = selectedLeads.filter((lead) => isRejectableStatus(lead.status));
  const brandCounts = new Map<string, number>();
  rejectableSelected.forEach((lead) => {
    brandCounts.set(lead.brand, (brandCounts.get(lead.brand) ?? 0) + 1);
  });
  const brandBreakdown = Array.from(brandCounts.entries())
    .map(([brand, count]) => ({ brand, count }))
    .sort((a, b) => b.count - a.count);

  return (
    <Dialog open={showRejectConfirm} onClose={cancelBulkReject} maxWidth="xs" fullWidth>
      <Box sx={{ p: "26px 28px" }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#1a1a1a" }}>
          Reject {rejectableSelected.length} lead{rejectableSelected.length === 1 ? "" : "s"}?
        </Typography>
        {/* Stronger warning than Report's — rejection has no reverse transition. */}
        <Alert severity="warning" sx={{ mt: 1.5, mb: 2.25 }}>
          This can't be undone — rejected leads won't be sent to their brand.
        </Alert>

        <Paper
          variant="outlined"
          sx={{ bgcolor: "#faf5fc", borderColor: "#f0e3f5", p: "6px 16px", maxHeight: 200, overflowY: "auto" }}
        >
          {brandBreakdown.map(({ brand, count }) => (
            <Box
              key={brand}
              sx={{ display: "flex", alignItems: "center", py: 1.25, borderBottom: "1px solid #f2e7f6" }}
            >
              <Typography sx={{ fontSize: 14, color: "#424242" }}>
                {count} lead{count === 1 ? "" : "s"} · {brand}
              </Typography>
            </Box>
          ))}
        </Paper>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.75, mt: 2.75 }}>
          <Button
            onClick={cancelBulkReject}
            disabled={isRejecting}
            sx={{ color: "#757575", letterSpacing: "0.4px" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmBulkReject}
            disabled={!canRejectLeads || isRejecting || rejectableSelected.length === 0}
            sx={{
              bgcolor: STATUS_META.Rejected.color,
              "&:hover": { bgcolor: "#38105F" },
              letterSpacing: "0.4px",
            }}
          >
            {isRejecting ? "Rejecting…" : "Reject"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
